import { getCurrentToken } from "../auth/tokenStore";
import {
  AdmissionPolicyRequest,
  AdmissionPolicyView,
  AdminUserView,
  AgentCardView,
  AgentRequestView,
  ChatLogEntryView,
  CohortBatchView,
  ConnectUrlView,
  ConversationView,
  EntitlementsView,
  EntitlementTierLimitView,
  EntitlementTierLimitUpdate,
  PlanView,
  ExternalEventView,
  ImportStatusView,
  IncomingConversationView,
  IntegrationsView,
  AnyMediaKind,
  MediaUploadTicket,
  MediaView,
  MeetingActionResponse,
  BlackoutView,
  ConnectionBox,
  ConnectionRequestStatus,
  ConnectionView,
  AvailabilityPatternView,
  AvailabilityPatternUpsertRequest,
  MeetingView,
  MentionCandidate,
  RescheduleRequest,
  MessageRequest,
  MessageResponse,
  TurnResponse,
  NegotiationStatus,
  PreviewInterest,
  ProfileSetupRequest,
  ProfileUpdateRequest,
  ProfileView,
  RelationshipView,
  SetTokenCapRequest,
  TokenUsageView,
  AdminPromptInfo,
  AdmissionPreviewResult,
  LlmExchange,
  LlmRoundSummary,
  LlmStoredTurn,
  LlmTurnRequest,
  LlmToolExchange,
  LlmToolResumeRequest,
  BookingPageView,
  BookingPageUpsertRequest,
  BookingPreviewSlot,
  BookingSlotStatus,
  DraftDescriptionRequest,
  DraftedDescriptionView,
  TokenUsageHeatmapView,
  PersonalTokenIssued,
  PersonalTokenView,
  BookingPageStatus,
  BookingEventView,
  BookingView,
  BookingSlotView,
  PublicBookingPageView,
  PublicBookingConfirmationView,
  PublicBookingManageView,
  BookSlotRequest,
  SetTierRequest,
  BookingTierLimitView,
  BookingTierLimitUpdateRequest,
  MyBookingLimits,
  RollingLinkView,
  RollingLinkUpsertRequest,
  RollingAppointmentSlotView,
  RollingAppointmentView,
  RollingLinkEvent,
  RollingLinkStatus,
  PublicRollingLinkView,
  SlotWindow,
  PublicRollingLinkSlot,
  BookRollingLinkRequest,
  PublicRollingLinkConfirmationView,
  RollingLinkManageView,
  RescheduleRollingLinkRequest,
  Poll,
  PollUpsertRequest,
  PollResults,
  PollEvent,
  PublicPoll,
  PollBallot,
  PollVoteRequest,
} from "./types";

// Empty in dev (Vite proxy handles routing) and in prod when the SPA is
// served from the same origin. Override via VITE_API_BASE for cross-origin
// deployments (e.g. CloudFront for the SPA, ALB for the API).
const BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: string,
  ) {
    super(message);
  }
}

/**
 * Thrown when the backend returns 401. The API client also dispatches a
 * global {@code "auth-expired"} event that {@code <TokenSync>} catches to
 * trigger silent renewal or full re-login.
 *
 * <p>Callers can either ignore it (the auth machinery handles recovery)
 * or surface a "session expired" toast.</p>
 */
export class AuthExpiredError extends ApiError {
  constructor(body: string) {
    super("Session expired", 401, body);
  }
}

/**
 * The request never produced an HTTP response — the connection dropped, DNS
 * failed, or it exceeded {@link DEFAULT_TIMEOUT_MS}. Distinct from
 * {@link ApiError} (which always carries a real status) so callers can tell
 * "we couldn't reach the server" apart from "the server said no".
 *
 * <p>This is the common failure on mobile: a radio handoff or a few seconds of
 * dead signal aborts the in-flight fetch. It is transient and retryable, which
 * a 404 is not.</p>
 */
export class NetworkError extends Error {
  constructor(public readonly cause?: unknown) {
    super("Could not reach the server");
    this.name = "NetworkError";
  }
}

/**
 * Wall-clock cap on a single attempt. Without one a stalled mobile connection
 * hangs the request indefinitely — `fetch` has no default timeout — and the
 * caller's spinner never resolves.
 */
const DEFAULT_TIMEOUT_MS = 15_000;

/** Backoff before each retry. Its length also caps how many retries are possible. */
const RETRY_BACKOFF_MS = [400, 1_000, 2_000];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** One attempt, with a timeout. Any non-HTTP failure surfaces as {@link NetworkError}. */
async function fetchOnce(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (e) {
    throw new NetworkError(e);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Centralised fetch wrapper. Injects {@code Authorization: Bearer ...}
 * from the token store on every call (so silent renewal is picked up
 * automatically), and turns 401s into {@link AuthExpiredError} +
 * dispatches the {@code "auth-expired"} event.
 *
 * <p>Every attempt is bounded by {@code timeoutMs}. Set {@code retries} to
 * re-attempt transient failures — network errors and 5xx only; a 4xx is a
 * definitive answer and is never retried. <b>Opt-in per call, and only safe on
 * idempotent requests</b>: retrying a POST could double-book a slot, so the
 * booking POST deliberately leaves it unset.</p>
 */
async function apiFetch(
  path: string,
  init?: RequestInit,
  opts?: { anonymous?: boolean; retries?: number; timeoutMs?: number },
): Promise<Response> {
  // Public endpoints (e.g. the invitee booking flow) must NOT carry a token: a
  // stale/expired one in localStorage would be rejected by the JWT filter with a
  // 401 before the permit-all rule applies. `anonymous` sends the request clean
  // and skips the session-expiry handling.
  const token = opts?.anonymous ? null : getCurrentToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Only set Content-Type if the caller is sending a body. GETs would
  // otherwise pre-flight unnecessarily.
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const url = `${BASE}${path}`;
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const retries = Math.min(opts?.retries ?? 0, RETRY_BACKOFF_MS.length);

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await sleep(RETRY_BACKOFF_MS[attempt - 1]);
    }
    let res: Response;
    try {
      res = await fetchOnce(url, { ...init, headers }, timeoutMs);
    } catch (e) {
      lastError = e;
      continue; // NetworkError — transient, worth another attempt.
    }
    // 5xx is the origin failing, not the request being wrong: retry if we can.
    if (res.status >= 500 && attempt < retries) {
      lastError = new ApiError(`${res.status} ${res.statusText}`, res.status, "");
      continue;
    }
    if (res.status === 401 && !opts?.anonymous) {
      const body = await res.text().catch(() => "");
      window.dispatchEvent(new Event("auth-expired"));
      throw new AuthExpiredError(body);
    }
    return res;
  }
  throw lastError ?? new NetworkError();
}

async function jsonOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(
      `${label} failed: ${res.status} ${res.statusText}`,
      res.status,
      body,
    );
  }
  return res.json() as Promise<T>;
}

/**
 * Human-readable message from a failed request. Prefers the backend's
 * ProblemDetail `detail` (RFC 7807) — so a validation error like
 * "max_bookings must be between 1 and 10" reaches the user instead of a bare
 * "400 Bad Request". Falls back through `message`, the raw body, then the status.
 */
export function apiErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (err instanceof ApiError) {
    try {
      const body = JSON.parse(err.body) as { detail?: unknown; message?: unknown };
      if (typeof body.detail === "string" && body.detail.trim()) return body.detail;
      if (typeof body.message === "string" && body.message.trim()) return body.message;
    } catch {
      // body wasn't JSON — fall through
    }
    if (err.body && err.body.length > 0 && err.body.length < 300) return err.body;
    return `${err.status}: ${err.message}`;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * GET /api/v1/profile — the current user's profile. The SPA's
 * ProfileProvider calls this once on first authenticated load; a null
 * {@code handle} gates the user onto /profile/setup.
 */
export async function getProfile(): Promise<ProfileView> {
  const res = await apiFetch("/api/v1/profile");
  return jsonOrThrow<ProfileView>(res, "GET /profile");
}

/** GET /api/v1/profile/token-usage — the current user's daily token usage for the profile heatmap. */
export async function getTokenUsageHeatmap(days = 182): Promise<TokenUsageHeatmapView> {
  const res = await apiFetch(`/api/v1/profile/token-usage?days=${days}`);
  return jsonOrThrow<TokenUsageHeatmapView>(res, "GET /profile/token-usage");
}

/**
 * POST /api/v1/profile — first-time setup. Sets the (immutable) handle and
 * optionally overrides the Cognito-captured names. Backend returns 409 if
 * the handle is taken or already set, 400 for format / reserved-word
 * violations.
 */
export async function setupProfile(req: ProfileSetupRequest): Promise<ProfileView> {
  const res = await apiFetch("/api/v1/profile", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<ProfileView>(res, "POST /profile");
}

/**
 * POST /api/v1/profile/terms — record that the user accepts the current Terms of Service.
 *
 * Returns the refreshed profile, so the gate can drop itself from the response rather than
 * firing a second GET. `version` is the value the server sent us in `termsVersionRequired`,
 * echoed back as a staleness check: if the terms changed while this bundle was cached, the
 * server 400s instead of recording acceptance of copy the user never saw.
 */
export async function acceptTerms(version: string): Promise<ProfileView> {
  const res = await apiFetch("/api/v1/profile/terms", {
    method: "POST",
    body: JSON.stringify({ version }),
  });
  return jsonOrThrow<ProfileView>(res, "POST /profile/terms");
}

/**
 * PATCH /api/v1/profile — update name fields. Handle is immutable in v1
 * (not patchable from the SPA either way).
 */
export async function updateProfile(req: ProfileUpdateRequest): Promise<ProfileView> {
  const res = await apiFetch("/api/v1/profile", {
    method: "PATCH",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<ProfileView>(res, "PATCH /profile");
}

/**
 * GET /api/v1/cards/{handle} — a user's public agent card. Works without
 * auth for public cards; platform-only cards need the bearer token (which
 * apiFetch attaches automatically when present) and 404 without it —
 * indistinguishable from a nonexistent handle by design.
 */
export async function getAgentCard(handle: string): Promise<AgentCardView> {
  const res = await apiFetch(`/api/v1/cards/${encodeURIComponent(handle)}`);
  return jsonOrThrow<AgentCardView>(res, "GET /cards/{handle}");
}

/**
 * POST /api/v1/profile/handle — the one-time rename for handles that
 * predate the letters+digits rule. 409 when the handle is taken or the
 * rename window is shut, 400 for format violations.
 */
export async function renameHandle(handle: string): Promise<ProfileView> {
  const res = await apiFetch("/api/v1/profile/handle", {
    method: "POST",
    body: JSON.stringify({ handle }),
  });
  return jsonOrThrow<ProfileView>(res, "POST /profile/handle");
}

/**
 * GET /api/v1/admin/users — every signed-up user, newest first. 403 unless
 * the caller is on the admin_user allowlist (the SPA also hides the page
 * behind {@code profile.admin}, but the backend check is the real gate).
 */
export async function listAdminUsers(): Promise<AdminUserView[]> {
  const res = await apiFetch("/api/v1/admin/users");
  return jsonOrThrow<AdminUserView[]>(res, "GET /admin/users");
}

/**
 * GET /api/v1/admin/users/{id} — a single user's full profile, read-only.
 * Same shape as the owner's GET /profile. Admin-only (403 otherwise).
 */
export async function getAdminUserProfile(userId: string): Promise<ProfileView> {
  const res = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}`);
  return jsonOrThrow<ProfileView>(res, "GET /admin/users/{id}");
}

/**
 * GET /api/v1/admin/users/{id}/preview-interest — a user's interest thread,
 * newest first. Admin-only.
 */
export async function getUserInterest(userId: string): Promise<PreviewInterest[]> {
  const res = await apiFetch(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/preview-interest`,
  );
  return jsonOrThrow<PreviewInterest[]>(res, "GET /admin/users/{id}/preview-interest");
}

/**
 * GET /api/v1/preview-interest — the current user's own interest thread.
 * Allowed even for not-yet-approved users (the waitlist shows it).
 */
export async function getMyInterest(): Promise<PreviewInterest[]> {
  const res = await apiFetch("/api/v1/preview-interest");
  return jsonOrThrow<PreviewInterest[]>(res, "GET /preview-interest");
}

/**
 * POST /api/v1/preview-interest — send a short note to the admins asking for
 * preview access. Returns the updated thread. 400 if empty/too long, 429 if
 * the daily limit is hit.
 */
export async function submitInterest(message: string): Promise<PreviewInterest[]> {
  const res = await apiFetch("/api/v1/preview-interest", {
    method: "POST",
    body: JSON.stringify({ message }),
  });
  return jsonOrThrow<PreviewInterest[]>(res, "POST /preview-interest");
}

/**
 * POST /api/v1/admin/users/{id}/enable — approve a user so they can use the
 * app. Returns the updated row. Admin-only.
 */
export async function enableUser(userId: string): Promise<AdminUserView> {
  const res = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/enable`, {
    method: "POST",
  });
  return jsonOrThrow<AdminUserView>(res, "POST /admin/users/{id}/enable");
}

/**
 * POST /api/v1/admin/users/{id}/disable — revoke a user's access. 409 if the
 * target is an admin (admins cannot be disabled). Returns the updated row.
 */
export async function disableUser(userId: string): Promise<AdminUserView> {
  const res = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/disable`, {
    method: "POST",
  });
  return jsonOrThrow<AdminUserView>(res, "POST /admin/users/{id}/disable");
}

/**
 * POST /api/v1/admin/users/{id}/token-cap — set/clear a user's daily/monthly
 * token caps (null or non-positive clears → unlimited). Returns the updated row.
 */
export async function setTokenCap(
  userId: string,
  req: SetTokenCapRequest,
): Promise<AdminUserView> {
  const res = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/token-cap`, {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<AdminUserView>(res, "POST /admin/users/{id}/token-cap");
}

/**
 * Upload one of the host's images.
 *
 * Three legs, and the middle one does not touch our API at all: we sign a URL, the browser PUTs the
 * bytes straight to storage, then we confirm. API Gateway caps a payload at 10MB, which is a hard
 * quota rather than a setting, and a 5MB banner already spends most of it.
 *
 * The PUT must send the returned headers verbatim: they are part of the signature, so an altered
 * content type or byte count is rejected by storage rather than by us.
 */
export async function uploadMedia(
  kind: AnyMediaKind,
  file: File,
  options: {
    /** Fraction of the bytes sent so far, 0 to 1. Called only while the bytes are in flight. */
    onProgress?: (fraction: number) => void;
    /** Aborts the transfer. The upload rejects with an {@code AbortError}. */
    signal?: AbortSignal;
  } = {},
): Promise<MediaView> {
  const ticketRes = await apiFetch(`/api/v1/media/${kind.toLowerCase()}/uploads`, {
    method: "POST",
    body: JSON.stringify({ content_type: file.type, bytes: file.size }),
  });
  const ticket = await jsonOrThrow<MediaUploadTicket>(ticketRes, `POST /media/${kind}/uploads`);

  await putSigned(ticket, file, options);

  const confirmRes = await apiFetch(`/api/v1/media/${kind.toLowerCase()}`, {
    method: "POST",
    body: JSON.stringify({ key: ticket.key }),
  });
  return jsonOrThrow<MediaView>(confirmRes, `POST /media/${kind}`);
}

/**
 * Send the bytes to storage, reporting progress.
 *
 * <p>{@code XMLHttpRequest} rather than {@code fetch}, for the one thing it still does better:
 * {@code upload.onprogress}. A fetch of a multi-megabyte photo is opaque until it finishes, which is
 * exactly the stretch of time a host most wants to see something happening — and it leaves no way to
 * offer a cancel that actually stops the transfer.</p>
 */
function putSigned(
  ticket: MediaUploadTicket,
  file: File,
  { onProgress, signal }: { onProgress?: (fraction: number) => void; signal?: AbortSignal },
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload cancelled.", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", ticket.url, true);

    // Every ticket header is part of the signature, so S3 rejects the PUT if one is missing or
    // altered — except Content-Length, which the browser forbids us to set and then sets itself from
    // the body. Same value, since the ticket was signed for exactly these bytes, so skipping it is
    // safe. It is skipped EXPLICITLY because the alternative is a spec-mandated silent no-op, and a
    // silent no-op over a signed header is the kind of thing that should be visible in the source.
    for (const [name, value] of Object.entries(ticket.headers)) {
      if (FORBIDDEN_HEADERS.has(name.toLowerCase())) continue;
      xhr.setRequestHeader(name, value);
    }

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      };
    }

    const abort = () => xhr.abort();
    signal?.addEventListener("abort", abort, { once: true });
    const done = () => signal?.removeEventListener("abort", abort);

    xhr.onload = () => {
      done();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Couldn't upload the image (${xhr.status}).`));
    };
    xhr.onerror = () => {
      done();
      reject(new Error("Couldn't reach storage."));
    };
    xhr.onabort = () => {
      done();
      reject(new DOMException("Upload cancelled.", "AbortError"));
    };

    xhr.send(file);
  });
}

/** Header names the browser sets itself and refuses to let script touch. */
const FORBIDDEN_HEADERS = new Set(["content-length"]);

/** DELETE /api/v1/media/{kind} — drop an image and fall back to the default. */
export async function removeMedia(kind: AnyMediaKind): Promise<MediaView> {
  const res = await apiFetch(`/api/v1/media/${kind.toLowerCase()}`, { method: "DELETE" });
  return jsonOrThrow<MediaView>(res, `DELETE /media/${kind}`);
}

/** GET /api/v1/token-usage — the caller's own consumption + caps (chat indicator). */
export async function getTokenUsage(): Promise<TokenUsageView> {
  const res = await apiFetch("/api/v1/token-usage");
  return jsonOrThrow<TokenUsageView>(res, "GET /token-usage");
}

/** GET /api/v1/entitlements — my effective tier, trial status, quota usage + features. */
export async function getEntitlements(): Promise<EntitlementsView> {
  const res = await apiFetch("/api/v1/entitlements");
  return jsonOrThrow<EntitlementsView>(res, "GET /entitlements");
}

/** GET /api/v1/entitlements/plans — the plan catalogue (quotas + features + link expiry). */
export async function getPlans(): Promise<PlanView[]> {
  const res = await apiFetch("/api/v1/entitlements/plans");
  return jsonOrThrow<PlanView[]>(res, "GET /entitlements/plans");
}

/** POST /api/v1/entitlements/upgrade — switch my plan (unpaid for now). */
export async function upgradePlan(tier: string): Promise<EntitlementsView> {
  const res = await apiFetch("/api/v1/entitlements/upgrade", {
    method: "POST",
    body: JSON.stringify({ tier }),
  });
  return jsonOrThrow<EntitlementsView>(res, "POST /entitlements/upgrade");
}

/** GET /api/v1/integrations — connected calendars + availability flag. */
export async function listIntegrations(): Promise<IntegrationsView> {
  const res = await apiFetch("/api/v1/integrations");
  return jsonOrThrow<IntegrationsView>(res, "GET /integrations");
}

/**
 * GET /api/v1/integrations/{provider}/connect — returns that provider's consent URL.
 * The caller navigates there with window.location.assign (a fetch carries
 * the bearer token; a browser navigation would not, hence URL-as-JSON).
 *
 * 409 when the host already has a different calendar connected.
 */
export async function getConnectUrl(provider: string): Promise<ConnectUrlView> {
  const path = `/api/v1/integrations/${provider.toLowerCase()}/connect`;
  const res = await apiFetch(path);
  return jsonOrThrow<ConnectUrlView>(res, `GET ${path}`);
}

/**
 * POST /api/v1/integrations/{provider}/import — pull a bounded forward window of
 * the user's events in for read-only display. Replaces any prior import.
 */
export async function importCalendar(provider: string): Promise<ImportStatusView> {
  const path = `/api/v1/integrations/${provider.toLowerCase()}/import`;
  const res = await apiFetch(path, { method: "POST" });
  return jsonOrThrow<ImportStatusView>(res, `POST ${path}`);
}

/** GET /api/v1/integrations/import-status — count + last import time. */
export async function getImportStatus(): Promise<ImportStatusView> {
  const res = await apiFetch("/api/v1/integrations/import-status");
  return jsonOrThrow<ImportStatusView>(res, "GET /integrations/import-status");
}

/** GET /api/v1/integrations/external-events — imported events overlapping [from, to). */
export async function getExternalEvents(
  from: string,
  to: string,
): Promise<ExternalEventView[]> {
  const res = await apiFetch(
    `/api/v1/integrations/external-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
  return jsonOrThrow<ExternalEventView[]>(res, "GET /integrations/external-events");
}

/** GET /api/v1/booking-pages/bookings — my confirmed bookings overlapping [from, to), for the calendar. */
export async function getMyBookings(
  from: string,
  to: string,
): Promise<BookingSlotView[]> {
  const res = await apiFetch(
    `/api/v1/booking-pages/bookings?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
  return jsonOrThrow<BookingSlotView[]>(res, "GET /booking-pages/bookings");
}

/** DELETE /api/v1/integrations/{provider} — revoke where supported, then forget it. */
export async function disconnectCalendar(provider: string): Promise<void> {
  const path = `/api/v1/integrations/${provider.toLowerCase()}`;
  const res = await apiFetch(path, { method: "DELETE" });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(
      `DELETE ${path} failed: ${res.status} ${res.statusText}`,
      res.status,
      body,
    );
  }
}

/** The caller's own availability patterns, oldest first. Exactly one is is_default. */
export async function listAvailabilityPatterns(): Promise<AvailabilityPatternView[]> {
  const res = await apiFetch("/api/v1/availability-patterns");
  return jsonOrThrow<AvailabilityPatternView[]>(res, "GET /availability-patterns");
}

/** Create an availability pattern. It is never the default; promote it explicitly. */
export async function createAvailabilityPattern(
  req: AvailabilityPatternUpsertRequest,
): Promise<AvailabilityPatternView> {
  const res = await apiFetch("/api/v1/availability-patterns", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<AvailabilityPatternView>(res, "POST /availability-patterns");
}

/** Rename a type or replace its availability. Omitted fields are left alone. */
export async function updateAvailabilityPattern(
  id: string,
  req: AvailabilityPatternUpsertRequest,
): Promise<AvailabilityPatternView> {
  const res = await apiFetch(`/api/v1/availability-patterns/${id}`, {
    method: "PATCH",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<AvailabilityPatternView>(res, "PATCH /availability-patterns/{id}");
}

/** Make this the default — what inbound invitations are checked against. */
export async function setDefaultAvailabilityPattern(id: string): Promise<void> {
  await noContent(
    apiFetch(`/api/v1/availability-patterns/${id}/default`, { method: "PUT" }),
    "PUT /availability-patterns/{id}/default",
  );
}

/**
 * Delete a type. 409 if it would leave you with none, or if it is the default
 * and no `promote` successor is named. People filed under it move to Uncategorized.
 */
export async function deleteAvailabilityPattern(id: string, promoteId?: string): Promise<void> {
  const qs = promoteId ? `?promote=${promoteId}` : "";
  await noContent(
    apiFetch(`/api/v1/availability-patterns/${id}${qs}`, { method: "DELETE" }),
    "DELETE /availability-patterns/{id}",
  );
}

/** File a person under one of my types. Additive — other types are untouched. */
export async function addAvailabilityPatternMember(typeId: string, peerUserId: string): Promise<void> {
  await noContent(
    apiFetch(`/api/v1/availability-patterns/${typeId}/members/${peerUserId}`, { method: "PUT" }),
    "PUT /availability-patterns/{id}/members/{peer}",
  );
}

/** Unfile a person; they fall back to Uncategorized rather than disappearing. */
export async function removeAvailabilityPatternMember(typeId: string, peerUserId: string): Promise<void> {
  await noContent(
    apiFetch(`/api/v1/availability-patterns/${typeId}/members/${peerUserId}`, { method: "DELETE" }),
    "DELETE /availability-patterns/{id}/members/{peer}",
  );
}

/** Await a 204-style response, throwing ApiError on failure (no body to parse). */
async function noContent(pending: Promise<Response>, what: string): Promise<void> {
  const res = await pending;
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(`${what} failed: ${res.status} ${res.statusText}`, res.status, body);
  }
}

export async function postMessage(req: MessageRequest): Promise<MessageResponse> {
  // Default timeZone to the browser's resolved IANA tz so the backend parser
  // can interpret times like "14:30" in the user's local clock. Callers can
  // override (e.g. tests passing an explicit zone).
  const body: MessageRequest = {
    ...req,
    timeZone: req.timeZone ?? browserTimeZone(),
  };
  const res = await apiFetch("/api/v1/messages", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return jsonOrThrow<MessageResponse>(res, "POST /messages");
}

/**
 * POST /api/v1/turns — the composer's one send path. The server decides which pipeline reads the
 * sentence (a still-sticky proposal folds it in; a closed `.{ }` selects the cohort interpreter;
 * anything else reaches the ordinary parser), and `channel` on the response says what happened.
 */
export async function postTurn(req: MessageRequest): Promise<TurnResponse> {
  const body: MessageRequest = {
    ...req,
    timeZone: req.timeZone ?? browserTimeZone(),
  };
  const res = await apiFetch("/api/v1/turns", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return jsonOrThrow<TurnResponse>(res, "POST /turns");
}

function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

export async function getConversation(id: string): Promise<ConversationView> {
  const res = await apiFetch(`/api/v1/conversations/${id}`);
  return jsonOrThrow<ConversationView>(res, `GET /conversations/${id}`);
}

/**
 * GET /api/v1/conversations/incoming — negotiations the user's agent is on as
 * a participant but did not initiate. Returns all OPEN ones plus anything
 * closed in the last ~60s. The homepage poller diffs successive snapshots of
 * this list to synthesise the ConversationInvited / terminal events the UI
 * renders (it replaced the per-user SSE feed).
 */
export async function listIncomingConversations(): Promise<
  IncomingConversationView[]
> {
  const res = await apiFetch("/api/v1/conversations/incoming");
  return jsonOrThrow<IncomingConversationView[]>(res, "GET /conversations/incoming");
}

/**
 * GET /api/v1/chat-log — the homepage chat history. Server merges user/agent
 * NL with A2A wire events filtered to conversations the user participates
 * in. Newest-first; pass {@code before} (ISO timestamp) for the next page
 * back in time. The SPA hydrates the homepage log on mount.
 */
export async function listChatLog(
  before?: string,
  limit = 50,
  conversationId?: string,
): Promise<ChatLogEntryView[]> {
  const params = new URLSearchParams();
  if (before) params.set("before", before);
  params.set("limit", String(limit));
  // Scope to one conversation server-side. The backend applies this filter
  // BEFORE truncating to `limit`, so an old meeting buried past the caller's
  // most-recent `limit` global entries still returns its own events — which a
  // client-side filter on an unscoped page cannot do.
  if (conversationId) params.set("conversationId", conversationId);
  // A cohort card carries meeting start times, so the log needs the caller's zone or they come
  // back at UTC offset. Harmless for every other row, which carries no wall-clock time.
  const zone = browserTimeZone();
  if (zone) params.set("time_zone", zone);
  const res = await apiFetch(`/api/v1/chat-log?${params.toString()}`);
  return jsonOrThrow<ChatLogEntryView[]>(res, "GET /chat-log");
}

// ---- Cohort intent (Pro; the propose / refine / confirm loop) ----

/**
 * POST /api/v1/cohort-intents — read a sentence, or narrow one already on screen.
 *
 * With no `batchId` this reads a fresh sentence. With one, the text is a reply that narrows that
 * sentence's still-open intents. **Neither acts on anything**: only `confirmCohortIntent` does.
 * The response may carry a `question` instead of cards, when the sentence was too vague to shape.
 */
export async function proposeCohortIntent(
  text: string,
  timeZone: string,
  batchId?: string,
  intentId?: string,
): Promise<CohortBatchView> {
  const res = await apiFetch("/api/v1/cohort-intents", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // intent_id scopes the reply to ONE card: only that intent is rewritten, its siblings keep
    // their state. Only meaningful alongside batch_id, and the server refuses it without one.
    body: JSON.stringify({ text, time_zone: timeZone, batch_id: batchId, intent_id: intentId }),
  });
  return jsonOrThrow<CohortBatchView>(res, "POST /cohort-intents");
}

/**
 * Confirm ONE intent. The only call here that changes anybody's calendar.
 *
 * Per intent, so the sentence's other cards stay open: a reschedule can be agreed while its sibling
 * cancel is still being refined.
 */
export async function confirmCohortIntent(
  batchId: string,
  intentId: string,
  timeZone: string,
): Promise<CohortBatchView> {
  const res = await apiFetch(
    `/api/v1/cohort-intents/batches/${batchId}/intents/${intentId}/confirm` +
      `?time_zone=${encodeURIComponent(timeZone)}`,
    { method: "POST" },
  );
  return jsonOrThrow<CohortBatchView>(res, "POST /cohort-intents confirm");
}

/** Drop ONE intent: the way out when refining has gone wrong is to throw it away and say it again. */
export async function dropCohortIntent(
  batchId: string,
  intentId: string,
): Promise<CohortBatchView> {
  // The same zone the other two cohort calls send: dropping one card returns the whole batch, and
  // the siblings still on screen carry meeting times.
  const zone = browserTimeZone();
  const res = await apiFetch(
    `/api/v1/cohort-intents/batches/${batchId}/intents/${intentId}` +
      (zone ? `?time_zone=${encodeURIComponent(zone)}` : ""),
    { method: "DELETE" },
  );
  return jsonOrThrow<CohortBatchView>(res, "DELETE /cohort-intents intent");
}

/**
 * PUT /api/v1/chat-log/clear — start a new thread. Advances the server-side
 * watermark so the homepage transcript reads empty (non-destructive).
 */
export async function clearChatLog(): Promise<void> {
  const res = await apiFetch("/api/v1/chat-log/clear", { method: "PUT" });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(
      `PUT /chat-log/clear failed: ${res.status} ${res.statusText}`,
      res.status,
      body,
    );
  }
}

export async function listMeetings(
  state?: NegotiationStatus,
): Promise<MeetingView[]> {
  const qs = state ? `?state=${state}` : "";
  const res = await apiFetch(`/api/v1/meetings${qs}`);
  return jsonOrThrow<MeetingView[]>(res, "GET /meetings");
}

/** Times the caller has ruled out for this meeting's chain (per-user; never the peer's). */
export async function listBlackouts(meetingId: string): Promise<BlackoutView[]> {
  const res = await apiFetch(`/api/v1/meetings/${meetingId}/blackouts`);
  return jsonOrThrow<BlackoutView[]>(res, "GET /meetings/{id}/blackouts");
}

/** People the caller has booked with, each carrying the caller's types they're filed under. */
export async function listRelationships(): Promise<RelationshipView[]> {
  const res = await apiFetch(`/api/v1/relationships`);
  return jsonOrThrow<RelationshipView[]>(res, "GET /relationships");
}

/**
 * @-mention autocomplete: booked-with people (most frequent first) then
 * publicly-visible handles matching `q`. An empty `q` returns the caller's
 * most-frequent contacts only.
 */
export async function searchMentionCandidates(
  q: string,
  limit = 5,
): Promise<MentionCandidate[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("limit", String(limit));
  const res = await apiFetch(`/api/v1/contacts/search?${params.toString()}`);
  return jsonOrThrow<MentionCandidate[]>(res, "GET /contacts/search");
}

// ---------- /connections ----------

/**
 * POST /api/v1/connections — send a connect request to a @handle. The reply is
 * uniform ("pending" | "accepted") whether or not the handle exists — the server
 * never reveals a handle's existence, so neither can the UI.
 */
export async function sendConnectRequest(
  handle: string,
): Promise<{ status: ConnectionRequestStatus }> {
  const res = await apiFetch("/api/v1/connections", {
    method: "POST",
    body: JSON.stringify({ handle }),
  });
  return jsonOrThrow<{ status: ConnectionRequestStatus }>(res, "POST /connections");
}

/** GET /api/v1/connections?box=received|sent|accepted */
export async function listConnections(box: ConnectionBox): Promise<ConnectionView[]> {
  const res = await apiFetch(`/api/v1/connections?box=${box}`);
  return jsonOrThrow<ConnectionView[]>(res, "GET /connections");
}

/**
 * GET /api/v1/connections/received/count — the bell summary: `pending` requests waiting for me
 * (the badge) and my total `accepted` connections (so the bell can spot a newly-accepted request).
 */
export async function getConnectionSummary(): Promise<{ pending: number; accepted: number }> {
  const res = await apiFetch("/api/v1/connections/received/count");
  return jsonOrThrow<{ pending: number; accepted: number }>(
    res,
    "GET /connections/received/count",
  );
}

export async function acceptConnection(id: string): Promise<ConnectionView> {
  const res = await apiFetch(`/api/v1/connections/${encodeURIComponent(id)}/accept`, {
    method: "POST",
  });
  return jsonOrThrow<ConnectionView>(res, "POST /connections/{id}/accept");
}

export async function declineConnection(id: string): Promise<ConnectionView> {
  const res = await apiFetch(`/api/v1/connections/${encodeURIComponent(id)}/decline`, {
    method: "POST",
  });
  return jsonOrThrow<ConnectionView>(res, "POST /connections/{id}/decline");
}

/**
 * DELETE /api/v1/connections/{id} — end a connection, from either side.
 *
 * Not gated, unlike sending one: V35 backfilled connections nobody agreed to, so the people most
 * likely to want out are exactly the ones without the paid feature.
 */
export async function revokeConnection(id: string): Promise<ConnectionView> {
  const res = await apiFetch(`/api/v1/connections/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  return jsonOrThrow<ConnectionView>(res, "DELETE /connections/{id}");
}

/** Cancel a meeting; the backend notifies the peer over A2A. */
export async function cancelMeeting(id: string): Promise<MeetingActionResponse> {
  const res = await apiFetch(`/api/v1/meetings/${id}/cancel`, { method: "POST" });
  return jsonOrThrow<MeetingActionResponse>(res, "POST /meetings/{id}/cancel");
}

/**
 * Ask for a new time. Opens a fresh negotiation; the meeting stays booked until a
 * new time is agreed, so an attempt that finds nothing leaves it untouched.
 */
export async function rescheduleMeeting(
  id: string,
  body: RescheduleRequest,
): Promise<MeetingActionResponse> {
  const res = await apiFetch(`/api/v1/meetings/${id}/reschedule`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return jsonOrThrow<MeetingActionResponse>(res, "POST /meetings/{id}/reschedule");
}

/**
 * Stop trying to move a meeting and keep it where it is. Cancels the in-flight
 * attempt and takes back the caller's own "I can't make this slot", so the original
 * time can be booked again. The meeting itself is untouched.
 */
export async function abandonReschedule(id: string): Promise<MeetingActionResponse> {
  const res = await apiFetch(`/api/v1/meetings/${id}/abandon-reschedule`, {
    method: "POST",
  });
  return jsonOrThrow<MeetingActionResponse>(res, "POST /meetings/{id}/abandon-reschedule");
}

/** Break an impasse: the initiator proposes new start time(s) to everyone. */
export async function resolveMeeting(
  id: string,
  startTimes: string[],
): Promise<MeetingActionResponse> {
  const res = await apiFetch(`/api/v1/meetings/${id}/resolve`, {
    method: "POST",
    body: JSON.stringify({ start_times: startTimes }),
  });
  return jsonOrThrow<MeetingActionResponse>(res, "POST /meetings/{id}/resolve");
}

// ---- Admin LLM playground (admin-only; 403 otherwise) ----

/** Mint a new correlation id ("round"). */
export async function startLlmRound(): Promise<{ correlationId: string }> {
  const res = await apiFetch("/api/v1/admin/llm/rounds", { method: "POST" });
  return jsonOrThrow<{ correlationId: string }>(res, "POST /admin/llm/rounds");
}

/** The shipped parser system prompt, for live editing in the playground. */
export async function getLlmSystemPrompt(): Promise<{ systemPrompt: string }> {
  const res = await apiFetch("/api/v1/admin/llm/system-prompt");
  return jsonOrThrow<{ systemPrompt: string }>(res, "GET /admin/llm/system-prompt");
}

/** Every DB-backed editable prompt with its live content + metadata. */
export async function getAdminPrompts(): Promise<AdminPromptInfo[]> {
  const res = await apiFetch("/api/v1/admin/llm/prompts");
  return jsonOrThrow<AdminPromptInfo[]>(res, "GET /admin/llm/prompts");
}

/** Save an edited prompt (takes effect immediately, no deploy). */
export async function saveAdminPrompt(key: string, content: string): Promise<AdminPromptInfo> {
  const res = await apiFetch(`/api/v1/admin/llm/prompts/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify({ content }),
  });
  return jsonOrThrow<AdminPromptInfo>(res, "PUT /admin/llm/prompts/{key}");
}

/** Revert a prompt to its bundled default. */
export async function resetAdminPrompt(key: string): Promise<AdminPromptInfo> {
  const res = await apiFetch(`/api/v1/admin/llm/prompts/${encodeURIComponent(key)}/reset`, {
    method: "POST",
  });
  return jsonOrThrow<AdminPromptInfo>(res, "POST /admin/llm/prompts/{key}/reset");
}

/** Preview the booking-description prompt: generate copy from a title with an unsaved override. */
export async function previewBookingPrompt(req: {
  title: string;
  availabilityPattern?: string;
  systemPrompt?: string;
}): Promise<{ markdown: string }> {
  const res = await apiFetch("/api/v1/admin/llm/prompts/booking-preview", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<{ markdown: string }>(res, "POST /admin/llm/prompts/booking-preview");
}

/**
 * Match a typed set of links against a request, through the real admission loop.
 *
 * <p>The links are typed rather than owned; everything else is what production runs. Correlated
 * into the current round so it appears in the history beside the parser turns.</p>
 */
export async function previewAdmissionPrompt(
  cid: string,
  req: {
    links: { title: string; description?: string }[];
    request: string;
    systemPrompt?: string;
    provider?: string;
    model?: string;
    temperature?: number;
  },
): Promise<AdmissionPreviewResult> {
  const res = await apiFetch(`/api/v1/admin/llm/rounds/${cid}/admission-preview`, {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<AdmissionPreviewResult>(res, "POST /admin/llm/rounds/{cid}/admission-preview");
}

/** Fire one turn at the chosen provider; returns the full exchange. */
export async function runLlmTurn(
  correlationId: string,
  req: LlmTurnRequest,
): Promise<LlmExchange> {
  const res = await apiFetch(
    `/api/v1/admin/llm/rounds/${correlationId}/turns`,
    { method: "POST", body: JSON.stringify(req) },
  );
  return jsonOrThrow<LlmExchange>(res, "POST /admin/llm/rounds/{cid}/turns");
}

/** List the caller's own past rounds within an optional date range. */
export async function listLlmRounds(
  from?: string,
  to?: string,
): Promise<LlmRoundSummary[]> {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const res = await apiFetch(`/api/v1/admin/llm/rounds${suffix}`);
  return jsonOrThrow<LlmRoundSummary[]>(res, "GET /admin/llm/rounds");
}

/** The stored turns of one round, in order. */
export async function getLlmRound(
  correlationId: string,
): Promise<LlmStoredTurn[]> {
  const res = await apiFetch(`/api/v1/admin/llm/rounds/${correlationId}`);
  return jsonOrThrow<LlmStoredTurn[]>(res, "GET /admin/llm/rounds/{cid}");
}

/** Fire one tool-mode turn (may pause for clarification). */
export async function runLlmToolTurn(
  correlationId: string,
  req: LlmTurnRequest,
): Promise<LlmToolExchange> {
  const res = await apiFetch(
    `/api/v1/admin/llm/rounds/${correlationId}/tool-turns`,
    { method: "POST", body: JSON.stringify(req) },
  );
  return jsonOrThrow<LlmToolExchange>(res, "POST /admin/llm/.../tool-turns");
}

/** Resume a paused tool loop with the answer + the conversation it returned. */
export async function resumeLlmToolTurn(
  correlationId: string,
  req: LlmToolResumeRequest,
): Promise<LlmToolExchange> {
  const res = await apiFetch(
    `/api/v1/admin/llm/rounds/${correlationId}/tool-turns/resume`,
    { method: "POST", body: JSON.stringify(req) },
  );
  return jsonOrThrow<LlmToolExchange>(res, "POST /admin/llm/.../tool-turns/resume");
}


// ---- Booking pages (#42) -------------------------------------------------

/** GET /api/v1/booking-pages — the host's own pages. */
export async function getMyBookingLimits(): Promise<MyBookingLimits> {
  const res = await apiFetch("/api/v1/booking-pages/limits");
  return jsonOrThrow<MyBookingLimits>(res, "GET /booking-pages/limits");
}

export async function listBookingPages(): Promise<BookingPageView[]> {
  const res = await apiFetch("/api/v1/booking-pages");
  return jsonOrThrow<BookingPageView[]>(res, "GET /booking-pages");
}

/**
 * POST /api/v1/booking-pages/draft-description — AI-draft a description from the title.
 * 402 if the caller isn't on a plan that includes it (the caller shows the upsell).
 */
export async function draftBookingDescription(
  req: DraftDescriptionRequest,
): Promise<DraftedDescriptionView> {
  const res = await apiFetch("/api/v1/booking-pages/draft-description", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<DraftedDescriptionView>(res, "POST /booking-pages/draft-description");
}

/** POST /api/v1/booking-pages — create a page. 409 if over the active-page limit. */
export async function createBookingPage(
  req: BookingPageUpsertRequest,
): Promise<BookingPageView> {
  const res = await apiFetch("/api/v1/booking-pages", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<BookingPageView>(res, "POST /booking-pages");
}

/**
 * POST /api/v1/booking-pages/preview — the bookable slots a draft page would offer right
 * now, with the owner's busy time already removed. Powers the builder's live preview.
 */
export async function previewBookingSlots(
  req: BookingPageUpsertRequest,
): Promise<BookingPreviewSlot[]> {
  const res = await apiFetch("/api/v1/booking-pages/preview", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<BookingPreviewSlot[]>(res, "POST /booking-pages/preview");
}

/**
 * GET /api/v1/booking-pages/{id}/slots — the page's frozen slots with their current state
 * (open / booked / blocked / past). A created page is immutable, so this is the owner's view.
 */
export async function getBookingPageSlots(id: string): Promise<BookingSlotStatus[]> {
  const res = await apiFetch(`/api/v1/booking-pages/${id}/slots`);
  return jsonOrThrow<BookingSlotStatus[]>(res, "GET /booking-pages/{id}/slots");
}

/** PUT /api/v1/booking-pages/{id}/status?value=ACTIVE|DISABLED. */
export async function setBookingPageStatus(
  id: string,
  value: BookingPageStatus,
): Promise<BookingPageView> {
  const res = await apiFetch(`/api/v1/booking-pages/${id}/status?value=${value}`, {
    method: "PUT",
  });
  return jsonOrThrow<BookingPageView>(res, "PUT /booking-pages/{id}/status");
}

/**
 * POST /api/v1/booking-pages/{id}/expire — end a link early. Non-destructive: it stays in the
 * owner's list and stays readable; it just stops taking bookings and 404s publicly.
 */
export async function expireBookingPage(id: string): Promise<BookingPageView> {
  const res = await apiFetch(`/api/v1/booking-pages/${id}/expire`, { method: "POST" });
  return jsonOrThrow<BookingPageView>(res, "POST /booking-pages/{id}/expire");
}

/** DELETE /api/v1/booking-pages/{id} — archive (soft-delete); the journal is kept. */
export async function deleteBookingPage(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/booking-pages/${id}`, { method: "DELETE" });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(`DELETE /booking-pages/{id} failed: ${res.status}`, res.status, body);
  }
}

/**
 * The activity drawer's page size.
 *
 * <p>Smaller than the server's own default on purpose. The server defaults large so that a caller
 * sending no page still receives what it always received; this client asks for real pages.</p>
 */
export const ACTIVITY_PAGE_SIZE = 25;

/**
 * `?page=&size=`, or nothing at all.
 *
 * <p>Omitting it is meaningful. The server then applies its own large default, which is what a
 * caller wanting the whole list relies on: RollingLinkBuilder draws every confirmed appointment
 * onto its calendar, and a page of 25 would silently stop drawing the 26th. Only the activity
 * drawer asks for pages.</p>
 */
function pageQuery(page?: number, size?: number): string {
  if (page === undefined && size === undefined) return "";
  return `?page=${Math.max(page ?? 0, 0)}&size=${Math.max(size ?? ACTIVITY_PAGE_SIZE, 1)}`;
}

/** GET /api/v1/booking-pages/{id}/bookings — who booked what. */
export async function listPageEvents(
  id: string,
  page?: number,
  size?: number,
): Promise<BookingEventView[]> {
  const res = await apiFetch(`/api/v1/booking-pages/${id}/events${pageQuery(page, size)}`);
  return jsonOrThrow<BookingEventView[]>(res, "GET /booking-pages/{id}/events");
}

export async function listPageBookings(
  id: string,
  page?: number,
  size?: number,
): Promise<BookingView[]> {
  const res = await apiFetch(`/api/v1/booking-pages/${id}/bookings${pageQuery(page, size)}`);
  return jsonOrThrow<BookingView[]>(res, "GET /booking-pages/{id}/bookings");
}

/**
 * POST /api/v1/booking-pages/{id}/bookings/{bookingId}/cancel — the host cancels one booking.
 * The optional note is shown to the invitee in their cancellation email.
 */
export async function cancelPageBooking(
  pageId: string,
  bookingId: string,
  note?: string,
): Promise<BookingView> {
  const res = await apiFetch(`/api/v1/booking-pages/${pageId}/bookings/${bookingId}/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note: note ?? null }),
  });
  return jsonOrThrow<BookingView>(res, "POST /booking-pages/{id}/bookings/{bookingId}/cancel");
}

/**
 * GET /api/v1/book/{slug} — the public invitee view. Sent anonymously (no token).
 *
 * <p>Retried: this is the first request an invitee's browser makes after following a
 * shared link, often on a phone, and a single dropped connection would otherwise leave
 * them on a dead error page with no way back. Safe to retry — it is a GET.</p>
 */
/**
 * Never send a window the link did not advertise: a range wholly outside its own is a 400 by
 * design, and the calendar is meant to stop at the edge rather than discover it by failing.
 */
function slotWindowQuery(window?: SlotWindow): string {
  if (!window) return "";
  return `?from=${encodeURIComponent(window.from)}&to=${encodeURIComponent(window.to)}`;
}
export async function getPublicBookingPage(
  slug: string,
  window?: SlotWindow,
): Promise<PublicBookingPageView> {
  const res = await apiFetch(
    `/api/v1/book/${encodeURIComponent(slug)}${slotWindowQuery(window)}`,
    undefined,
    { anonymous: true, retries: 2 },
  );
  return jsonOrThrow<PublicBookingPageView>(res, "GET /book/{slug}");
}

/** POST /api/v1/book/{slug} — take a slot. Sent anonymously (no token). */
export async function bookSlot(
  slug: string,
  req: BookSlotRequest,
): Promise<PublicBookingConfirmationView> {
  const res = await apiFetch(
    `/api/v1/book/${encodeURIComponent(slug)}`,
    { method: "POST", body: JSON.stringify(req) },
    { anonymous: true },
  );
  return jsonOrThrow<PublicBookingConfirmationView>(res, "POST /book/{slug}");
}

/** GET /api/v1/book/manage/{token} — the invitee's self-service view. Anonymous, token-gated. */
export async function getBookingManage(token: string): Promise<PublicBookingManageView> {
  const res = await apiFetch(`/api/v1/book/manage/${encodeURIComponent(token)}`, undefined, {
    anonymous: true,
  });
  return jsonOrThrow<PublicBookingManageView>(res, "GET /book/manage/{token}");
}

/** POST /api/v1/book/manage/{token}/cancel — cancel a booking via the emailed link. Anonymous. */
export async function cancelBooking(token: string): Promise<PublicBookingManageView> {
  const res = await apiFetch(
    `/api/v1/book/manage/${encodeURIComponent(token)}/cancel`,
    { method: "POST" },
    { anonymous: true },
  );
  return jsonOrThrow<PublicBookingManageView>(res, "POST /book/manage/{token}/cancel");
}

// ---- Booking tiers + admin (#42, Increment 3) ----------------------------

/** POST /api/v1/admin/users/{id}/tier — set a user's subscription tier. */
export async function setUserTier(userId: string, req: SetTierRequest): Promise<AdminUserView> {
  const res = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/tier`, {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<AdminUserView>(res, "POST /admin/users/{id}/tier");
}

/** GET /api/v1/admin/users/{id}/booking-pages — a user's pages (admin view). */
export async function getUserBookingPages(userId: string): Promise<BookingPageView[]> {
  const res = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/booking-pages`);
  return jsonOrThrow<BookingPageView[]>(res, "GET /admin/users/{id}/booking-pages");
}

/** GET /api/v1/admin/booking-pages/{pageId}/bookings — a page's bookings (admin view). */
export async function getAdminPageBookings(pageId: string): Promise<BookingView[]> {
  const res = await apiFetch(`/api/v1/admin/booking-pages/${encodeURIComponent(pageId)}/bookings`);
  return jsonOrThrow<BookingView[]>(res, "GET /admin/booking-pages/{id}/bookings");
}

/** GET /api/v1/admin/booking-limits — the per-tier caps. */
export async function getBookingLimits(): Promise<BookingTierLimitView[]> {
  const res = await apiFetch("/api/v1/admin/booking-limits");
  return jsonOrThrow<BookingTierLimitView[]>(res, "GET /admin/booking-limits");
}

/** PUT /api/v1/admin/booking-limits/{tier} — tune one tier's caps. */
export async function updateBookingLimit(
  tier: string,
  req: BookingTierLimitUpdateRequest,
): Promise<BookingTierLimitView> {
  const res = await apiFetch(`/api/v1/admin/booking-limits/${encodeURIComponent(tier)}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<BookingTierLimitView>(res, "PUT /admin/booking-limits/{tier}");
}

/** GET /api/v1/admin/entitlement-limits — the per-tier quotas + feature flags. */
export async function getEntitlementLimits(): Promise<EntitlementTierLimitView[]> {
  const res = await apiFetch("/api/v1/admin/entitlement-limits");
  return jsonOrThrow<EntitlementTierLimitView[]>(res, "GET /admin/entitlement-limits");
}

/** PUT /api/v1/admin/entitlement-limits/{tier} — tune one tier's quotas/features. */
export async function updateEntitlementLimit(
  tier: string,
  req: EntitlementTierLimitUpdate,
): Promise<EntitlementTierLimitView> {
  const res = await apiFetch(`/api/v1/admin/entitlement-limits/${encodeURIComponent(tier)}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<EntitlementTierLimitView>(res, "PUT /admin/entitlement-limits/{tier}");
}


// ---- Rolling links (booking offering #2) -------------------------------

/** GET /api/v1/rolling-links — the host's own rolling links, newest first. */
export async function listRollingLinks(): Promise<RollingLinkView[]> {
  const res = await apiFetch("/api/v1/rolling-links");
  return jsonOrThrow<RollingLinkView[]>(res, "GET /rollingLinks");
}

/** POST /api/v1/rolling-links — create a rolling link. 409 if the handle is already taken by this owner. */
export async function createRollingLink(req: RollingLinkUpsertRequest): Promise<RollingLinkView> {
  const res = await apiFetch("/api/v1/rolling-links", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<RollingLinkView>(res, "POST /rollingLinks");
}

/** POST /api/v1/rolling-links/preview — bookable slots a draft rolling link would offer now (busy removed). */
export async function previewRollingLinkSlots(req: RollingLinkUpsertRequest): Promise<PublicRollingLinkSlot[]> {
  const res = await apiFetch("/api/v1/rolling-links/preview", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<PublicRollingLinkSlot[]>(res, "POST /rollingLinks/preview");
}

/** GET /api/v1/rolling-links/{id}. */
export async function getRollingLink(id: string): Promise<RollingLinkView> {
  const res = await apiFetch(`/api/v1/rolling-links/${id}`);
  return jsonOrThrow<RollingLinkView>(res, "GET /rollingLinks/{id}");
}

/** PUT /api/v1/rolling-links/{id} — a rolling link is mutable, so edits go here. */
export async function updateRollingLink(id: string, req: RollingLinkUpsertRequest): Promise<RollingLinkView> {
  const res = await apiFetch(`/api/v1/rolling-links/${id}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<RollingLinkView>(res, "PUT /rollingLinks/{id}");
}

/** PUT /api/v1/rolling-links/{id}/status — enable or disable a rolling link. */
export async function setRollingLinkStatus(id: string, status: RollingLinkStatus): Promise<RollingLinkView> {
  const res = await apiFetch(`/api/v1/rolling-links/${id}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
  return jsonOrThrow<RollingLinkView>(res, "PUT /rollingLinks/{id}/status");
}

/** DELETE /api/v1/rolling-links/{id} — disable the rolling link (existing appointments are kept). */
export async function deleteRollingLink(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/rolling-links/${id}`, { method: "DELETE" });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(`DELETE /rollingLinks/{id} failed: ${res.status}`, res.status, body);
  }
}

/** GET /api/v1/rolling-links/{id}/appointments — the booked appointments on a rolling link. */
export async function listRollingAppointments(
  id: string,
  page?: number,
  size?: number,
): Promise<RollingAppointmentView[]> {
  const res = await apiFetch(`/api/v1/rolling-links/${id}/appointments${pageQuery(page, size)}`);
  return jsonOrThrow<RollingAppointmentView[]>(res, "GET /rollingLinks/{id}/appointments");
}

/**
 * POST /api/v1/rolling-links/{id}/appointments/{appointmentId}/cancel — the host cancels one
 * appointment. Cancels that appointment only, never the rest of its group.
 */
export async function cancelLinkAppointment(
  linkId: string,
  appointmentId: string,
  note?: string,
): Promise<RollingAppointmentView> {
  const res = await apiFetch(
    `/api/v1/rolling-links/${linkId}/appointments/${appointmentId}/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: note ?? null }),
    },
  );
  return jsonOrThrow<RollingAppointmentView>(
    res,
    "POST /rolling-links/{id}/appointments/{appointmentId}/cancel",
  );
}

/**
 * GET /api/v1/rolling-links/appointments — my confirmed rolling appointments overlapping
 * [from, to), for the meetings calendar. The rolling twin of {@link getMyBookings}.
 */
export async function getMyRollingAppointments(
  from: string,
  to: string,
): Promise<RollingAppointmentSlotView[]> {
  const res = await apiFetch(
    `/api/v1/rolling-links/appointments?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
  return jsonOrThrow<RollingAppointmentSlotView[]>(res, "GET /rolling-links/appointments");
}

/** GET /api/v1/rolling-links/{id}/events — the rolling link's journal (bookings + config changes), newest first. */
export async function listRollingLinkEvents(
  id: string,
  page?: number,
  size?: number,
): Promise<RollingLinkEvent[]> {
  const res = await apiFetch(`/api/v1/rolling-links/${id}/events${pageQuery(page, size)}`);
  return jsonOrThrow<RollingLinkEvent[]>(res, "GET /rollingLinks/{id}/events");
}

/**
 * GET /api/v1/rolling-link/by-handle/{owner}/{rolling link} — the public invitee view. Anonymous, retried
 * (the first request from a shared link, often on a phone).
 */
export async function getPublicRollingLink(
  ownerHandle: string,
  rollingLinkHandle: string,
  window?: SlotWindow,
): Promise<PublicRollingLinkView> {
  const res = await apiFetch(
    `/api/v1/rolling-link/by-handle/${encodeURIComponent(ownerHandle)}/${encodeURIComponent(rollingLinkHandle)}${slotWindowQuery(window)}`,
    undefined,
    { anonymous: true, retries: 2 },
  );
  return jsonOrThrow<PublicRollingLinkView>(res, "GET /rolling-link/by-handle/{owner}/{rollingLink}");
}

/** POST /api/v1/rolling-link/by-handle/{owner}/{rolling link}/book — take one or more slots. Anonymous. */
export async function bookRollingLinkSlots(
  ownerHandle: string,
  rollingLinkHandle: string,
  req: BookRollingLinkRequest,
): Promise<PublicRollingLinkConfirmationView> {
  const res = await apiFetch(
    `/api/v1/rolling-link/by-handle/${encodeURIComponent(ownerHandle)}/${encodeURIComponent(rollingLinkHandle)}/book`,
    { method: "POST", body: JSON.stringify(req) },
    { anonymous: true },
  );
  return jsonOrThrow<PublicRollingLinkConfirmationView>(res, "POST /rolling-link/by-handle/{owner}/{rollingLink}/book");
}

/** GET /api/v1/rolling-link/manage/{token} — the invitee's self-service view. Anonymous, token-gated. */
export async function getRollingLinkManage(token: string): Promise<RollingLinkManageView> {
  const res = await apiFetch(`/api/v1/rolling-link/manage/${encodeURIComponent(token)}`, undefined, {
    anonymous: true,
  });
  return jsonOrThrow<RollingLinkManageView>(res, "GET /rolling-link/manage/{token}");
}

/** POST /api/v1/rolling-link/manage/{token}/cancel — cancel via the emailed link. Anonymous. */
export async function cancelRollingAppointment(token: string): Promise<RollingLinkManageView> {
  const res = await apiFetch(
    `/api/v1/rolling-link/manage/${encodeURIComponent(token)}/cancel`,
    { method: "POST" },
    { anonymous: true },
  );
  return jsonOrThrow<RollingLinkManageView>(res, "POST /rolling-link/manage/{token}/cancel");
}

/** POST /api/v1/rolling-link/manage/{token}/reschedule — atomically move to a new slot. Anonymous. */
export async function rescheduleRollingAppointment(
  token: string,
  req: RescheduleRollingLinkRequest,
): Promise<RollingLinkManageView> {
  const res = await apiFetch(
    `/api/v1/rolling-link/manage/${encodeURIComponent(token)}/reschedule`,
    { method: "POST", body: JSON.stringify(req) },
    { anonymous: true },
  );
  return jsonOrThrow<RollingLinkManageView>(res, "POST /rolling-link/manage/{token}/reschedule");
}

// ---- Poll (booking offering #3) ----------------------------------------------
//
// Host calls sit under the PLURAL /polls and need a JWT; the invitee calls sit under the SINGULAR
// /poll and are anonymous. That split is what the server's public-path allowlist keys on, so
// mixing the two up here would either 401 an invitee or expose a host route.

/** GET /api/v1/polls — the caller's polls, newest first. */
export async function listPolls(): Promise<Poll[]> {
  const res = await apiFetch("/api/v1/polls");
  return jsonOrThrow<Poll[]>(res, "GET /polls");
}

/** GET /api/v1/polls/{id} */
export async function getPoll(id: string): Promise<Poll> {
  const res = await apiFetch(`/api/v1/polls/${id}`);
  return jsonOrThrow<Poll>(res, "GET /polls/{id}");
}

/** POST /api/v1/polls */
export async function createPoll(req: PollUpsertRequest): Promise<Poll> {
  const res = await apiFetch("/api/v1/polls", { method: "POST", body: JSON.stringify(req) });
  return jsonOrThrow<Poll>(res, "POST /polls");
}

/** PUT /api/v1/polls/{id} — a poll stays editable while it is open. */
export async function updatePoll(id: string, req: PollUpsertRequest): Promise<Poll> {
  const res = await apiFetch(`/api/v1/polls/${id}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<Poll>(res, "PUT /polls/{id}");
}

/** PUT /api/v1/polls/{id}/status — open or close voting. */
export async function setPollStatus(id: string, status: "OPEN" | "CLOSED"): Promise<Poll> {
  const res = await apiFetch(`/api/v1/polls/${id}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
  return jsonOrThrow<Poll>(res, "PUT /polls/{id}/status");
}

/** DELETE /api/v1/polls/{id} — soft delete; releases the calendar event and recalls any invite. */
export async function deletePoll(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/polls/${id}`, { method: "DELETE" });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(`DELETE /polls/{id} failed: ${res.status}`, res.status, body);
  }
}

/** GET /api/v1/polls/{id}/results — who responded and what they chose, with tallies. */
export async function getPollResults(id: string): Promise<PollResults> {
  const res = await apiFetch(`/api/v1/polls/${id}/results`);
  return jsonOrThrow<PollResults>(res, "GET /polls/{id}/results");
}

/** GET /api/v1/polls/{id}/events — the poll's journal, newest first. */
export async function listPollEvents(
  id: string,
  page?: number,
  size?: number,
): Promise<PollEvent[]> {
  const res = await apiFetch(`/api/v1/polls/${id}/events${pageQuery(page, size)}`);
  return jsonOrThrow<PollEvent[]>(res, "GET /polls/{id}/events");
}

/** POST /api/v1/polls/{id}/confirm — pick the winning time. Sends NOTHING. */
export async function confirmPoll(id: string, optionId: string): Promise<Poll> {
  const res = await apiFetch(`/api/v1/polls/${id}/confirm`, {
    method: "POST",
    body: JSON.stringify({ option_id: optionId }),
  });
  return jsonOrThrow<Poll>(res, "POST /polls/{id}/confirm");
}

/** DELETE /api/v1/polls/{id}/confirm — withdraw it (cancels the calendar entry if announced). */
export async function unconfirmPoll(id: string): Promise<Poll> {
  const res = await apiFetch(`/api/v1/polls/${id}/confirm`, { method: "DELETE" });
  return jsonOrThrow<Poll>(res, "DELETE /polls/{id}/confirm");
}

/**
 * POST /api/v1/polls/{id}/confirmation-email — send, or resend, the confirmation with its calendar
 * invite. Separate from confirming on purpose: the host decides when everyone finds out.
 */
export async function sendPollConfirmation(id: string): Promise<Poll> {
  const res = await apiFetch(`/api/v1/polls/${id}/confirmation-email`, { method: "POST" });
  return jsonOrThrow<Poll>(res, "POST /polls/{id}/confirmation-email");
}

/** GET /api/v1/poll/by-slug/{slug} — the anonymous invitee view. No counts on this one, ever. */
export async function getPublicPoll(slug: string): Promise<PublicPoll> {
  const res = await apiFetch(`/api/v1/poll/by-slug/${encodeURIComponent(slug)}`, undefined, {
    anonymous: true,
  });
  return jsonOrThrow<PublicPoll>(res, "GET /poll/by-slug/{slug}");
}


/** POST /api/v1/poll/by-slug/{slug}/vote — the receipt carries the token to change it later. */
export async function votePoll(slug: string, req: PollVoteRequest): Promise<PollBallot> {
  const res = await apiFetch(
    `/api/v1/poll/by-slug/${encodeURIComponent(slug)}/vote`,
    { method: "POST", body: JSON.stringify(req) },
    { anonymous: true },
  );
  return jsonOrThrow<PollBallot>(res, "POST /poll/by-slug/{slug}/vote");
}

/** GET /api/v1/poll/manage/{token} — your own answers. Anonymous, token-gated. */
export async function getPollBallot(token: string): Promise<PollBallot> {
  const res = await apiFetch(`/api/v1/poll/manage/${encodeURIComponent(token)}`, undefined, {
    anonymous: true,
  });
  return jsonOrThrow<PollBallot>(res, "GET /poll/manage/{token}");
}

/** POST /api/v1/poll/manage/{token} — change your answers. Anonymous, token-gated. */
export async function amendPollBallot(token: string, req: PollVoteRequest): Promise<PollBallot> {
  const res = await apiFetch(
    `/api/v1/poll/manage/${encodeURIComponent(token)}`,
    { method: "POST", body: JSON.stringify(req) },
    { anonymous: true },
  );
  return jsonOrThrow<PollBallot>(res, "POST /poll/manage/{token}");
}

// ------------------------------------------------------- admission (A2A marketplace)

/** A pattern's admission policy, with the vocabulary it is written in. Pro and above. */
export async function getAdmissionPolicy(patternId: string): Promise<AdmissionPolicyView> {
  const res = await apiFetch(`/api/v1/availability-patterns/${patternId}/admission`);
  return jsonOrThrow<AdmissionPolicyView>(res, "GET /availability-patterns/{id}/admission");
}

/**
 * Replace the whole policy. A rule that cannot mean what it says comes back 422 with the reason
 * written in the same words as the rule; the editor puts it back into the sentence.
 */
export async function saveAdmissionPolicy(
  patternId: string,
  req: AdmissionPolicyRequest,
): Promise<AdmissionPolicyView> {
  const res = await apiFetch(`/api/v1/availability-patterns/${patternId}/admission`, {
    method: "PUT",
    body: JSON.stringify(req),
  });
  return jsonOrThrow<AdmissionPolicyView>(res, "PUT /availability-patterns/{id}/admission");
}

// ------------------------------------------------------- agent requests (A2A marketplace)

/** The host's own inbox, newest first. */
export async function listAgentRequests(page = 0, size = 25): Promise<AgentRequestView[]> {
  const res = await apiFetch(`/api/v1/agent-requests?page=${page}&size=${size}`);
  return jsonOrThrow<AgentRequestView[]>(res, "GET /agent-requests");
}

/** How many are waiting on a decision. What the nav badge counts; a lapsed hold is not one. */
export async function getAgentRequestsWaiting(): Promise<number> {
  const res = await apiFetch("/api/v1/agent-requests/waiting");
  const body = await jsonOrThrow<{ count: number }>(res, "GET /agent-requests/waiting");
  return body.count;
}

/** Book a held request. Goes through exactly the path an automatic booking takes. */
export async function approveAgentRequest(holdId: string): Promise<{ status: string }> {
  const res = await apiFetch(`/api/v1/agent-requests/holds/${holdId}/approve`, { method: "POST" });
  return jsonOrThrow<{ status: string }>(res, "POST /agent-requests/holds/{id}/approve");
}

/** Release the hour and tell them it could not be made. No reason travels. */
export async function declineAgentRequest(holdId: string): Promise<{ status: string }> {
  const res = await apiFetch(`/api/v1/agent-requests/holds/${holdId}/decline`, { method: "POST" });
  return jsonOrThrow<{ status: string }>(res, "POST /agent-requests/holds/{id}/decline");
}

/**
 * Record that you got in touch about a request nobody could take.
 *
 * <p>Records only that you dealt with it. The getting in touch happens in your own mail client, to
 * an address the person agreed to share; nothing is sent from here and they are told nothing by
 * us (D-94).</p>
 */
export async function answeredAgentRequest(considerationId: string): Promise<{ status: string }> {
  const res = await apiFetch(`/api/v1/agent-requests/considerations/${considerationId}/answered`, {
    method: "POST",
  });
  return jsonOrThrow<{ status: string }>(res, "POST /agent-requests/considerations/{id}/answered");
}

/** Close one on your side. The record stays; only the prompt goes. They are told nothing. */
export async function dismissAgentRequest(considerationId: string): Promise<{ status: string }> {
  const res = await apiFetch(`/api/v1/agent-requests/considerations/${considerationId}/dismiss`, {
    method: "POST",
  });
  return jsonOrThrow<{ status: string }>(res, "POST /agent-requests/considerations/{id}/dismiss");
}

/**
 * Call off a booked meeting. Keyed on the meeting, not on the hold: a rule that booked outright
 * never took one, and those are the bookings a host is most likely to want to undo.
 */
export async function cancelAgentRequest(reference: string): Promise<{ status: string }> {
  const res = await apiFetch(`/api/v1/agent-requests/${encodeURIComponent(reference)}/cancel`, {
    method: "POST",
  });
  return jsonOrThrow<{ status: string }>(res, "POST /agent-requests/{reference}/cancel");
}

// ------------------------------------------------------- personal access tokens (MCP 3)

/** The caller's tokens, newest first, secrets nowhere. */
export async function listPersonalTokens(): Promise<PersonalTokenView[]> {
  const res = await apiFetch("/api/v1/tokens");
  return jsonOrThrow<PersonalTokenView[]>(res, "GET /tokens");
}

/**
 * Mint a token. The response carries the secret ONCE; it cannot be retrieved again by any route.
 * An empty scope list gets the server's read-only default.
 */
export async function createPersonalToken(
  name: string,
  scopes: string[],
): Promise<PersonalTokenIssued> {
  const res = await apiFetch("/api/v1/tokens", {
    method: "POST",
    body: JSON.stringify({ name, scopes }),
  });
  return jsonOrThrow<PersonalTokenIssued>(res, "POST /tokens");
}

/** Revoke immediately. The row survives for audit; it just stops working. */
export async function revokePersonalToken(id: string): Promise<PersonalTokenView> {
  const res = await apiFetch(`/api/v1/tokens/${id}`, { method: "DELETE" });
  return jsonOrThrow<PersonalTokenView>(res, "DELETE /tokens/{id}");
}
